export const parseBBCode = (
  input: string = '',
  replace: boolean = true,
): string => {
  // Prevent null or undefined input
  if (!input) return '';

  const replacements: {
    regex: RegExp;
    replace: string | ((...args: string[]) => string);
  }[] = [
    // Cleanup rules
    {
      regex: /\n{3,}/gis,
      replace: '\n',
    },
    {
      regex: /"]\n{1,}/gis,
      replace: '"]',
    },
    // Basic formatting
    {
      regex: /\[b\](.*?)\[\/b\]/gis,
      replace: replace ? '<strong>$1</strong>' : '$1',
    },
    {
      regex: /\[i\](.*?)\[\/i\]/gis,
      replace: replace ? '<em>$1</em>' : '$1',
    },
    {
      regex: /\[u\](.*?)\[\/u\]/gis,
      replace: replace ? '<u>$1</u>' : '$1',
    },
    {
      regex: /\[s\](.*?)\[\/s\]/gis,
      replace: replace ? '<del>$1</del>' : '$1',
    },
    {
      regex: /\[url(?:(?:=['"](.*?)['"])|(?:.*?))?\](.*?)\[\/url\]/gis,
      replace: replace
        ? (_match: string, url: string, text: string) => {
            try {
              const parsedUrl = url || text;

              new URL(parsedUrl);

              return `<a href="${parsedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            } catch {
              return text;
            }
          }
        : '',
    },
    {
      regex: /\[email\](.*?)\[\/email\]/gis,
      replace: '$1',
    },
    {
      regex: /\[img(.*?)\](.*?)\[\/img\]/gis,
      replace: replace ? '<img src="$2" alt="Image" />' : '$2',
    },
    {
      regex: /\[quote="?([^,]+).*?\](.*?)\[\/quote\]/gis,
      replace: replace
        ? '<blockquote><cite>$1 said:</cite><div>$2</div></blockquote>'
        : '$2',
    },
    {
      regex: /\[media=(twitter|instagram|youtube)\](.*?)\[\/media\]/gis,
      replace: replace ? '[$1]$2[/$1]' : '',
    },
    {
      regex: /\[twitter\](.*?)\[\/twitter\]/gis,
      replace: replace
        ? '<div class="x-post" id="x-post-$1" data-post-id="$1" data-media-loading="true"></div>'
        : '',
    },
    {
      regex: /\[instagram\](.*?)\[\/instagram\]/gis,
      replace: replace
        ? '<blockquote class="instagram-media" data-instgrm-version="14" data-instgrm-permalink="https://www.instagram.com/p/$1/" data-media-loading="true"><a href="https://www.instagram.com/p/$1/" target="_blank"></a></blockquote>'
        : '',
    },
    {
      regex: /\[youtube\](.*?)\[\/youtube\]/gis,
      replace: replace
        ? '<div class="youtube-media" id="player-$1" data-video-id="$1" data-media-loading="true"></div>'
        : '',
    },
    {
      regex: /\[user(.*?)\](.*?)\[\/user\]/gis,
      replace: '<strong>$2</strong>',
    },
    {
      regex: /\[list\](.*?)\[\/list\]/gis,
      replace: replace
        ? (_match: string, content: string) => {
            const items = content
              .split(/\[\*\]/)
              .map((item) => item.trim())
              .filter((item) => item.length > 0)
              .map((item) => `<li>${item}</li>`)
              .join('');

            return `<ul>${items}</ul>`;
          }
        : '$1',
    },
    // Handle unsupported tags
    {
      regex:
        /\[(color|font|size|spoiler|code|indent)(?:=[^\]]+)?\](.*?)\[\/(color|font|size|spoiler|code|indent)\]/gis,
      replace: '$2',
    },
    {
      regex: /\[(attach)(.*?)\](.*?)\[\/(attach)\]/gis,
      replace: '',
    },
    {
      regex: /\[media=(\w+)\](.*?)\[\/media\]/gis,
      replace: replace
        ? '<div data-unsupported="true">Preview this content on our boards.</div>'
        : '',
    },
  ];

  let output = input;
  let changed = true;

  // Process replacements with nesting support
  while (changed) {
    changed = false;

    for (const { regex, replace: replacement } of replacements) {
      if (regex.test(output)) {
        output = output.replace(regex, replacement as any);
        changed = true;
      }
    }
  }

  // Sanitize output to prevent XSS
  if (replace) {
    output = output.replace(/[<]\s*script\s*[>]/gi, '<script-blocked>');
  }

  return output.trim();
};

/**
 * Parses a menu item title to extract a tag and the clean title.
 * @param title - Menu item title, potentially containing a tag in the format {{ tag }}
 * @returns An object containing the tag and the clean title.
 */
export const parseMenuTitle = (
  title?: string | null,
): { tag: string | null; title: string } => {
  if (!title) {
    return { title: '', tag: null };
  }

  const cleanTitle = title?.replace(/\{\{(.+?)\}\}/, '').trim() || '';
  const tag = title?.match(/\{\{(.+?)\}\}/)?.[1]?.trim() || null;

  return { title: cleanTitle, tag };
};

export const parseTemplateVariables = (
  input: string = '',
  variables: Record<string, string> = {},
): string => {
  let output = input;

  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');

    output = output.replace(regex, value);
  }

  return output;
};
